import { createFileRoute, Link } from "@tanstack/react-router";
import { TerminalModuleStrip } from "@/components/terminal/TerminalModuleStrip"
import {
  ArrowRight,
  ShieldX,
  AlertTriangle,
  CheckCircle2,
  Brain,
  Activity,
  ScanSearch,
  Sparkles,
} from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { DropZoneVisual } from "@/components/structurelens/DropZoneVisual";
import { OutputCardVisual } from "@/components/structurelens/OutputCardVisual";
import { HeroAnimatedArtifact } from "@/components/structurelens/HeroAnimatedArtifact";
import { ExampleReadingSection } from "@/components/structurelens/ExampleReadingSection";
import { ComparisonTable } from "@/components/structurelens/ComparisonTable";
import { KnowledgeBaseSection } from "@/components/structurelens/KnowledgeBaseSection";
import { FaqSection, STRUCTURELENS_FAQ } from "@/components/structurelens/FaqSection";
import { StickyConversionBar } from "@/components/structurelens/StickyConversionBar";

const SITE = "https://xauproacademy.com";
const PAGE_URL = `${SITE}/platform/structurelens`;
const TITLE = "AI Chart Analysis for Gold (XAUUSD) | StructureLens | XAU Pro Academy";
const DESCRIPTION =
  "Institutional SMC chart analysis for XAUUSD. Upload a gold chart and receive a fixed-schema reading: structure, liquidity, displacement, POI, risk, grade.";

const KB_SLUGS = [
  "liquidity-sweep-gold-trading",
  "ict-displacement-explained",
  "order-blocks-fvg-explained",
  "choch-vs-bos-smart-money",
  "inducement-trap-idm-smc",
  "high-probability-entry-criteria-gold",
];

export const Route = createFileRoute("/platform/structurelens")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "StructureLens: AI Chart Analysis for Gold" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@XAUProAcademy" },
      { name: "twitter:title", content: "StructureLens: AI Chart Analysis for Gold" },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to run an institutional SMC reading on a gold chart",
          description: "Upload an XAUUSD chart to StructureLens and receive a fixed-schema SMC reading.",
          step: [
            { "@type": "HowToStep", position: 1, name: "Capture the chart", text: "Take a clean XAUUSD screenshot from TradingView or MetaTrader on H4, H1 or M15, in PNG, JPG or WEBP." },
            { "@type": "HowToStep", position: 2, name: "Upload to StructureLens", text: "Open StructureLens in your account and upload the chart image." },
            { "@type": "HowToStep", position: 3, name: "Review the reading", text: "Read the bias, structure, liquidity, POI, risk, concerns and doctrine grade. A no valid setup result is a legitimate outcome." },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "StructureLens",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          description: DESCRIPTION,
          url: PAGE_URL,
          featureList: [
            "Bias detection",
            "Market structure (BOS, CHoCH, MSS)",
            "Liquidity mapping (BSL, SSL, IDM)",
            "Displacement assessment",
            "Point of interest qualification (OB, FVG, Breaker)",
            "Risk and invalidation framing",
            "Session and macro context",
            "Doctrine grading (A+, A, B, C)",
          ],
          audience: {
            "@type": "Audience",
            audienceType: "Gold traders, SMC practitioners, funded account traders",
          },
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            description: "Free account, one analysis per week",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Platform", item: `${SITE}/platform` },
            { "@type": "ListItem", position: 3, name: "StructureLens", item: PAGE_URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: STRUCTURELENS_FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "XAU Pro Academy Knowledge Base, referenced by StructureLens",
          itemListElement: KB_SLUGS.map((slug, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE}/education/${slug}`,
          })),
        }),
      },
    ],
  }),
  component: StructureLensPage,
});

function StructureLensPage() {
  const { user } = useAuth();
  const freeTo = user ? "/dashboard/analyst" : "/auth";
  const heroLabel = user ? "Open StructureLens" : "Get your first institutional reading free";

  return (
    <Layout>
      <TerminalModuleStrip module="StructureLens™" />
      {/* Hero */}
      <section className="overflow-x-clip border-b border-border/60 bg-background">
        <div className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid min-w-0 items-start justify-items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:justify-items-stretch">

            <div className="w-full min-w-0 max-w-full overflow-hidden">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                Platform · Capability 01
              </div>
              <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-foreground lg:text-6xl">
                Institutional AI chart analysis for gold.
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground lg:text-lg">
                StructureLens reads your XAUUSD chart through the same SMC
                doctrine that constrains Orion. Structure, liquidity,
                displacement, session, risk. Returned in a fixed institutional
                format.
              </p>
              <div className="mt-8 flex w-full max-w-full flex-col items-stretch gap-3 overflow-hidden sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:overflow-visible">
                <Button asChild variant="gold" size="lg" className="w-full min-w-0 max-w-[calc(100vw-32px)] justify-center px-4 text-center sm:w-auto sm:max-w-none sm:px-8">
                  <Link
                    to={freeTo}
                    {...(!user ? { search: { tab: "signup" } } : {})}
                  >
                    <ArrowRight className="h-4 w-4 opacity-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1 text-center leading-tight whitespace-normal sm:flex-none sm:whitespace-nowrap">{heroLabel}</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <a
                  href="#why-not-generic"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-gold"
                >
                  How it differs from generic AI <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
              {!user && (
                <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  Free account · no card · one analysis per week
                </div>
              )}
            </div>
            <div className="min-w-0 w-full max-w-[358px] sm:max-w-none">
              <HeroAnimatedArtifact />
            </div>
          </div>
        </div>
      </section>

      {/* Example Reading: show value before asking */}
      <ExampleReadingSection />

      {/* Doctrine Constraint */}
      <section className="border-b border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            The doctrine constraint
          </div>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            What StructureLens refuses to do is what makes it useful.
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            The same hard rejection rules that govern Orion also govern
            StructureLens. A reading is either valid under doctrine, or it is
            not returned.
          </p>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-destructive/30 bg-background/40 p-6">
              <ShieldX className="h-5 w-5 text-destructive" />
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-destructive/90">
                Will reject
              </div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                <li>No displacement.</li>
                <li>No liquidity interaction.</li>
                <li>Mitigated or over-traded POI.</li>
                <li>Wick-only structure break.</li>
              </ul>
            </div>
            <div className="rounded-xl border border-amber-500/30 bg-background/40 p-6">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300/90">
                Will downgrade
              </div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                <li>Counter-trend without sweep.</li>
                <li>Weak RR or tight invalidation.</li>
                <li>Unstable macro window.</li>
                <li>Dead-liquidity session.</li>
              </ul>
            </div>
            <div className="rounded-xl border border-gold/40 bg-background/40 p-6">
              <CheckCircle2 className="h-5 w-5 text-gold" />
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                Will publish
              </div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                <li>HTF aligned, displacement confirmed.</li>
                <li>Liquidity interaction present.</li>
                <li>Strong POI, session aligned.</li>
                <li>Graded A or A+ under doctrine.</li>
              </ul>
            </div>
          </div>

          <p className="mt-8 max-w-2xl text-sm italic text-muted-foreground">
            Generic models optimize for an answer. StructureLens optimizes for a
            correct answer, or none.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-border/60">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            How it works
          </div>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            One chart in. One structured reading out.
          </h2>

          <div className="mt-10 grid items-center gap-6 md:grid-cols-[1fr_auto_1fr]">
            <DropZoneVisual />
            <div className="hidden items-center justify-center text-gold md:flex">
              <ArrowRight className="h-6 w-6" />
            </div>
            <OutputCardVisual />
          </div>

          <p className="mt-6 max-w-2xl text-sm text-muted-foreground">
            Same output schema, every analysis. Bias, structure, liquidity, POI,
            risk, concerns, grade.
          </p>
        </div>
      </section>

      {/* Mid-page conversion checkpoint */}
      <section className="border-b border-border/60 bg-background">
        <div className="container mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-6">
          <div className="text-sm text-foreground/90">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
              Try it
            </span>
            <span className="mx-2 text-muted-foreground/60">·</span>
            <span>Want to see this on your own chart? Free account, no card.</span>
          </div>
          <Button asChild variant="gold" size="sm">
            <Link to={freeTo} {...(!user ? { search: { tab: "signup" } } : {})}>
              Start free <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Why not generic AI + comparison table */}
      <section id="why-not-generic" className="border-b border-border/60 bg-slate-deep scroll-mt-20">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            StructureLens vs generic AI chart tools
          </div>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            A general assistant will read your chart. It will not read it the same way.
          </h2>
          <p className="mt-4 max-w-2xl text-sm text-muted-foreground">
            StructureLens reasons from the XAU Pro Academy framework, returns a
            fixed institutional schema, and applies hard rejection rules. The
            differences are structural, not stylistic.
          </p>

          <div className="mt-10">
            <ComparisonTable />
          </div>

          <div className="mt-8">
            <Link
              to="/validation"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:underline"
            >
              See the validation evidence <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Knowledge Base authority */}
      <KnowledgeBaseSection />

      {/* StructureLens vs Orion */}
      <section className="border-b border-border/60">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            StructureLens vs Orion
          </div>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            Shared doctrine. Different jobs.
          </h2>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-gold/40 bg-background/40 p-6">
              <div className="flex items-center gap-2">
                <ScanSearch className="h-5 w-5 text-gold" />
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
                  StructureLens
                </span>
              </div>
              <div className="mt-4 font-display text-lg font-semibold text-foreground">
                User initiated. On your chart.
              </div>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li>You upload the chart.</li>
                <li>You receive a structured analysis.</li>
                <li>Consumes an analysis credit.</li>
                <li>Does not publish signals.</li>
              </ul>
            </div>
            <Link
              to="/orion"
              className="group rounded-xl border border-border/60 bg-background/40 p-6 transition hover:border-gold/40"
            >
              <div className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-gold" />
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                  Orion
                </span>
              </div>
              <div className="mt-4 font-display text-lg font-semibold text-foreground">
                Market initiated. On its own data.
              </div>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li>Scans the market continuously.</li>
                <li>Publishes a decision only when doctrine is satisfied.</li>
                <li>No chart uploads. No chat.</li>
                <li>Does not analyze uploaded images.</li>
              </ul>
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-gold">
                Read about Orion <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      {/* Free access */}
      <section className="border-b border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-4xl px-4 py-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold-muted/20 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            <Sparkles className="h-3.5 w-3.5" /> Free access
          </div>
          <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-foreground lg:text-4xl">
            Your first institutional reading is free.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground">
            One analysis per week on a free account. The cap is intentional.
            Quality over volume is the doctrine, on the desk and on the page.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <span className="rounded-sm border border-border/60 bg-background/40 px-2 py-1">No card</span>
            <span className="rounded-sm border border-border/60 bg-background/40 px-2 py-1">No trial timer</span>
            <span className="rounded-sm border border-border/60 bg-background/40 px-2 py-1">Cancel anytime</span>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to={freeTo} {...(!user ? { search: { tab: "signup" } } : {})}>
                {heroLabel} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Membership CTA */}
      <section>
        <div className="container mx-auto max-w-4xl px-4 py-20 text-center">
          <Activity className="mx-auto h-6 w-6 text-gold" />
          <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-foreground lg:text-4xl">
            Operate inside the system.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground">
            Membership lifts the weekly limit and opens the rest of Platform:
            Journal, Risk Calculator, and Orion access.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/pricing">View Membership</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/platform">Back to Platform</Link>
            </Button>
          </div>
        </div>
      </section>

      <StickyConversionBar />
      {/* Tail spacer so sticky bar never covers final CTA on short viewports */}
      <div className="h-16" aria-hidden />
    </Layout>
  );
}
